import {useEffect, useMemo, useState} from "react";
import {Box, Combobox, HStack, Span, Stack, Text, Wrap, useFilter, useListCollection} from "@chakra-ui/react";
import {PencilIcon, PlusIcon, UserGroupIcon, UserIcon} from "@heroicons/react/20/solid";
import type {OwnerPayload, ReviewerRole} from "platformed-client/types";
import {Portal} from "../../../components/Portal";
import {Button} from "../../../components/ui/button";
import {Checkbox} from "../../../components/ui/checkbox";
import {Tag} from "../../../components/ui/tag";
import {Avatar} from "../../../components/ui/avatar";
import TeamAvatar from "../../../components/TeamAvatar";
import {
  buildCandidates,
  isEmptyPlan,
  planApply,
  selectedContentIds,
  type ApplyOutcome,
  type ApplyPlan,
  type Candidate,
  type ContentId,
} from "./reviewCandidates";
import type {ReviewMap} from "./reviewsShared";
import {ReviewStatusText} from "./ReviewerRow";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";

/** Hoisted: `useFilter` memoises on this object's identity, so an inline literal rebuilds the
 * collection every render and flickers the list while typing. */
const FILTER_OPTIONS = {sensitivity: "base"} as const;

const TEAM_COLOR_PALETTE = "blue";

/**
 * Assigns one role. Everyone already holding it starts selected, so adding and removing are the
 * same gesture; anyone holding a different role on this page is listed but disabled, since a
 * person holds at most one role per page.
 */
export const AddReviewersPopover = ({
  role,
  reviews,
  roles,
  owners,
  reviewerCount,
  descendantPageCount,
  staged,
  stagedRoles,
  onApply,
}: {
  role: ReviewerRole;
  /** Every review on the page — needed to tell "already this role" from "already another role". */
  reviews: ReviewMap;
  roles: ReviewerRole[];
  owners: OwnerPayload[];
  reviewerCount: number;
  descendantPageCount: number;
  /** Changes already queued for this role, so reopening resumes rather than restarts. */
  staged?: ApplyPlan;
  /** Roles queued elsewhere on this surface, so people already claimed can't be picked twice. */
  stagedRoles?: Map<ContentId, ReviewerRole>;
  onApply: (plan: ApplyPlan) => Promise<ApplyOutcome>;
}) => {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  const candidates = useMemo(
    () => buildCandidates({owners, reviews, roles, roleId: role.reviewer_role_id, stagedRoles}),
    [owners, reviews, roles, role.reviewer_role_id, stagedRoles],
  );

  /** Who holds this role right now, allowing for changes staged but not yet written. */
  const baseSelection = useMemo(
    () => (staged ? staged.fullSelection.map(c => c.contentId) : selectedContentIds(candidates)),
    [staged, candidates],
  );
  const [draft, setDraft] = useState<ContentId[]>(baseSelection);
  const [addToChildren, setAddToChildren] = useState(staged?.addToChildren ?? false);
  const [submitting, setSubmitting] = useState(false);

  const {contains} = useFilter(FILTER_OPTIONS);
  const {collection, filter, set} = useListCollection<Candidate>({
    initialItems: candidates,
    filter: contains,
    itemToValue: candidate => candidate.contentId,
    itemToString: candidate => candidate.searchText,
    isItemDisabled: candidate => candidate.state.type === "OtherRole" || candidate.state.type === "StagedOtherRole",
    groupBy: candidate => candidate.kind,
    groupSort: ["User", "Team"],
  });

  /*
    `useListCollection` snapshots `initialItems`, and the draft's initial value is read once, but
    every role's picker stays mounted for the life of the page — so both would go on describing
    the page as it first rendered. Resync only while closed: an open picker must not have its
    list reordered or its draft discarded under the user.
  */
  useEffect(() => {
    if (open) return;
    set(candidates);
    setDraft(baseSelection);
  }, [open, candidates, baseSelection, set]);

  const byContentId = useMemo(() => new Map(candidates.map(c => [c.contentId, c])), [candidates]);
  const selected = draft.flatMap(id => {
    const candidate = byContentId.get(id);
    return candidate ? [candidate] : [];
  });

  const plan = planApply({candidates, draft, role, addToChildren});
  const nothingToDo = isEmptyPlan(plan);

  // Closing discards the draft; the resync effect restores it from what the page actually holds.
  const close = () => {
    setOpen(false);
    filter("");
  };

  // Only a settled change closes the picker, so backing out of a confirmation leaves the draft
  // where the user left it.
  const submit = async () => {
    setSubmitting(true);
    try {
      if ((await onApply(plan)) === "applied") close();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Combobox.Root
      open={open}
      onOpenChange={e => (e.open ? setOpen(true) : close())}
      multiple
      collection={collection}
      value={draft}
      onValueChange={e => setDraft(e.value as ContentId[])}
      onInputValueChange={e => filter(e.inputValue)}
      positioning={{placement: "bottom-end", sameWidth: false, overflowPadding: 12}}
      width="auto"
      // The trigger slot carries the size variant's own type scale, so this must match the
      // trigger Button's size or the two fight over font-size and gap.
      size="xs"
    >
      <Combobox.Control>
        <Combobox.Trigger focusable asChild>
          <Button size="2xs" variant="outline" colorPalette="gray">
            {reviewerCount > 0 ? <PencilIcon /> : <PlusIcon />}
            {reviewerCount > 0 ? <T keyName="c.Edit_reviewers" /> : <T keyName="c.Add_reviewers" />}
          </Button>
        </Combobox.Trigger>
      </Combobox.Control>

      {/* `textStyle` puts the panel back on the reading scale, which `size="xs"` shrinks. */}
      <Portal>
        <Combobox.Positioner>
          <Combobox.Content w="sm" px={0} py={0} maxH="none" colorPalette="purple" textStyle="sm">
            <Box borderBottomWidth="1px" borderColor="border">
              {/* Stripped of its border and ring so it reads as the panel's header. Padding is set
                  here because the recipe takes inline-end padding from `--combobox-input-padding-end`,
                  declared on the control slot — this input is in the portalled content, so it never
                  resolves and the end padding would collapse to zero. */}
              <Combobox.Input
                placeholder={t("c.Search_people_and_teams[ellipsis]")}
                borderWidth={0}
                rounded={0}
                textStyle="sm"
                minH={10}
                px={3}
                _focusVisible={{outline: "none", boxShadow: "none"}}
              />

              {selected.length > 0 && (
                <Wrap gap={1} px={3} pb={2}>
                  {selected.map(candidate => (
                    <ReviewerTag
                      key={candidate.contentId}
                      candidate={candidate}
                      onRemove={() => setDraft(draft.filter(id => id !== candidate.contentId))}
                    />
                  ))}
                </Wrap>
              )}
            </Box>

            <Box maxH="64" overflowY="auto" py={1}>
              <Combobox.Empty>
                <Text fontSize="sm" color="fg.muted" px={3} py={3} textAlign="center">
                  <T keyName="tprm.reviews.add_reviewers.no_matches" />
                </Text>
              </Combobox.Empty>

              {collection.group().map(([kind, items]) => (
                <Combobox.ItemGroup key={kind}>
                  <Combobox.ItemGroupLabel>
                    {kind === "Team" ? <T keyName="c.Teams" /> : <T keyName="c.Users" />}
                  </Combobox.ItemGroupLabel>
                  {items.map(candidate => (
                    <Combobox.Item key={candidate.contentId} item={candidate}>
                      <HStack gap={2} flex={1} minW={0}>
                        <CandidateIdentity candidate={candidate} />
                        {/* The row is disabled, and naming the role they already hold is the whole
                        explanation — a person holds at most one role per page. */}
                        {candidate.state.type === "OtherRole" && (
                          <Tag size="sm" colorPalette="gray">
                            <T keyName="c.Already_a_role[parameterized]" params={{role: candidate.state.role.name}} />
                          </Tag>
                        )}
                        {candidate.state.type === "StagedOtherRole" && (
                          <Tag size="sm" colorPalette="gray">
                            <T keyName="c.Being_added_as[parameterized]" params={{role: candidate.state.role.name}} />
                          </Tag>
                        )}
                        {candidate.state.type === "ThisRole" && (
                          <ReviewStatusText status={candidate.state.review.review_status} />
                        )}
                      </HStack>
                      <Combobox.ItemIndicator />
                    </Combobox.Item>
                  ))}
                </Combobox.ItemGroup>
              ))}
            </Box>

            <Stack gap={2} p={3} borderTopWidth="1px" borderColor="border" bg="bg.subtle">
              {descendantPageCount > 0 && (
                <Checkbox size="sm" checked={addToChildren} onCheckedChange={e => setAddToChildren(!!e.checked)}>
                  <Span fontSize="xs">
                    <T keyName="tprm.reviews.add_reviewers.cascade_hint[count]" params={{count: descendantPageCount}} />
                  </Span>
                </Checkbox>
              )}
              <HStack gap={2}>
                {/* Blank until there is a change to report — the picker explains itself. */}
                <HStack gap={2} flex={1} minW={0} fontSize="xs" fontWeight="semibold">
                  {plan.added.length > 0 && (
                    <Span color="green.fg">
                      <T keyName="c.to_request[count]" params={{count: plan.added.length}} />
                    </Span>
                  )}
                  {plan.removed.length > 0 && (
                    <Span color="red.fg">
                      <T keyName="c.to_remove[count]" params={{count: plan.removed.length}} />
                    </Span>
                  )}
                </HStack>
                <Button
                  size="xs"
                  variant="outline"
                  colorPalette="gray"
                  bg="bg.panel"
                  onClick={close}
                  disabled={submitting}
                >
                  <T keyName="c.Cancel" />
                </Button>
                <Button
                  size="xs"
                  colorPalette="green"
                  onClick={submit}
                  loading={submitting}
                  disabled={submitting || nothingToDo}
                >
                  <T keyName="c.Apply" />
                </Button>
              </HStack>
            </Stack>
          </Combobox.Content>
        </Combobox.Positioner>
      </Portal>
    </Combobox.Root>
  );
};

/** Identity at picker-row scale; `OwnerAvatar` is sized for the reviewer list. */
const CandidateIdentity = ({candidate}: {candidate: Candidate}) => (
  <>
    {candidate.kind === "Team" ? (
      // Same tokens as the tag a team gets once selected, so row and chip read as one thing.
      <TeamAvatar size="2xs" colorPalette={TEAM_COLOR_PALETTE} bg="colorPalette.subtle" color="colorPalette.fg" />
    ) : (
      <Avatar
        size="2xs"
        name={candidate.name}
        src={candidate.owner.type === "User" ? (candidate.owner.content.avatar_url ?? undefined) : undefined}
      />
    )}
    <Text>{candidate.name}</Text>
  </>
);

const ReviewerTag = ({candidate, onRemove}: {candidate: Candidate; onRemove: () => void}) => (
  <Tag
    size="md"
    colorPalette={candidate.kind === "Team" ? TEAM_COLOR_PALETTE : "gray"}
    // Bare icon: the start-element slot sizes its own icon, and an explicit size fights it.
    startElement={candidate.kind === "Team" ? <UserGroupIcon /> : <UserIcon />}
    closable
    closeTriggerProps={{
      onClick: event => {
        event.stopPropagation();
        onRemove();
      },
    }}
  >
    {candidate.name}
  </Tag>
);
